import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { BlockRenderer } from '../renderer';
import { findBlock, updateBlockInTree } from '../utils/blockUtils';
import { renderIcon } from '../../SprintDetail/FlowchartView/utils/renderIcon';
import { snippetTypeLabel } from '../utils/snippetUtils';

const noop = () => {};

/**
 * Modal preview of embedded source block(s) — real DocEditor chrome, read-only.
 * Toggle open/close is allowed so collapsed sections can be inspected.
 */
export const SnippetPreviewModal = ({
  sourceDocTitle,
  sourceDocIcon,
  sourceBlock,
  sourceBlocks,
  onClose,
  onOpenDoc,
  sourceDocId,
  resolveDocMeta,
  resolveVariable,
}) => {
  const initial = sourceBlocks?.length
    ? sourceBlocks
    : sourceBlock
      ? [sourceBlock]
      : [];
  const [viewBlocks, setViewBlocks] = useState(initial);

  useEffect(() => {
    setViewBlocks(
      sourceBlocks?.length ? sourceBlocks : sourceBlock ? [sourceBlock] : []
    );
  }, [sourceBlock, sourceBlocks]);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      e.stopPropagation();
      onClose();
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [onClose]);

  const handleUpdate = (id, updates) => {
    if (!updates?.props || !('isOpen' in updates.props)) return;
    setViewBlocks((prev) => {
      const tree = updateBlockInTree(prev, id, {
        props: {
          ...(findBlock(prev, id)?.props || {}),
          isOpen: updates.props.isOpen,
        },
      });
      return tree;
    });
  };

  const blockDefault = (e) => {
    if ((e.ctrlKey || e.metaKey) && ['c', 'a', 'f'].includes(e.key.toLowerCase())) {
      return;
    }
    if (e.key === 'Escape' || e.key === 'Tab') return;
    e.preventDefault();
    e.stopPropagation();
  };

  if (!viewBlocks.length) return null;

  const typeLabel =
    viewBlocks.length === 1
      ? snippetTypeLabel(viewBlocks[0])
      : `${viewBlocks.length} blocks`;

  return createPortal(
    <div
      className="snippet-preview-modal"
      role="dialog"
      aria-modal="true"
      aria-label="Snippet preview"
    >
      <button
        type="button"
        className="snippet-preview-modal__backdrop"
        aria-label="Close"
        onClick={onClose}
      />
      <div className="snippet-preview-modal__panel">
        <div className="snippet-preview-modal__header">
          <div className="snippet-preview-modal__title">
            <span className="snippet-preview-modal__icon">
              {renderIcon(sourceDocIcon, {
                size: '16px',
                fallback: <span>📄</span>,
              })}
            </span>
            <span>{sourceDocTitle || 'Untitled Doc'}</span>
            <span className="snippet-preview-modal__type">· {typeLabel}</span>
          </div>
          <div className="snippet-preview-modal__actions">
            {onOpenDoc && sourceDocId && (
              <button
                type="button"
                className="snippet-preview-modal__btn"
                onClick={() => {
                  onOpenDoc(sourceDocId);
                  onClose();
                }}
              >
                Open doc
              </button>
            )}
            <button
              type="button"
              className="snippet-preview-modal__btn snippet-preview-modal__btn--ghost"
              onClick={onClose}
            >
              Close
            </button>
          </div>
        </div>
        <div
          className="snippet-preview-modal__body doc-editor doc-editor--readonly"
          onBeforeInput={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onKeyDown={blockDefault}
          onPaste={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onCut={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          <div className="doc-editor-blocks">
            {viewBlocks.map((b, i) => (
              <BlockRenderer
                key={b.id}
                block={b}
                prevType={viewBlocks[i - 1]?.type}
                nextType={viewBlocks[i + 1]?.type}
                onUpdate={handleUpdate}
                onInsertAfter={noop}
                onUpdateAndInsert={noop}
                onDelete={noop}
                onMergeWithPrevious={noop}
                onMergeWithNext={noop}
                onNavigate={noop}
                onEscapeContainer={noop}
                onOpenDoc={onOpenDoc}
                resolveDocMeta={resolveDocMeta}
                resolveVariable={resolveVariable}
                listNumber={b?.type === 'number' ? 1 : undefined}
              />
            ))}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};
